import { Controller } from "@hotwired/stimulus";
import animateCSS from "../utils/animateCSS";

export default class extends Controller {
  indexCounter = 1;

  declare readonly hasTemplateValue: boolean;
  declare readonly templateValue: string;
  declare readonly hasObjectNameValue: boolean;
  declare readonly objectNameValue: string;
  declare readonly hasAssociationNameValue: boolean;
  declare readonly associationNameValue: string;
  declare readonly hasInitialIndexValue: boolean;
  declare readonly initialIndexValue: number;
  declare readonly hasActiveCssClassValue: boolean;
  declare readonly activeCssClassValue: string;

  declare readonly hasFieldsContainerTarget: boolean;
  declare readonly fieldsContainerTarget: HTMLDivElement;

  static values = {
    template: String,
    objectName: String,
    associationName: String,
    initialIndex: Number,
    activeCssClass: String
  };

  static targets = ["fieldsContainer", "nestedForm"];

  initialize() {
    this.setNestedFormActive = this.setNestedFormActive.bind(this);
  }

  connect() {
    this.indexCounter = this.initialIndexValue;
  }

  addFields(e) {
    e.preventDefault();

    this.addNestedField();
  }

  nestedFormTargetConnected(nestedForm) {
    if (this.activeCssClassValue) {
      nestedForm.addEventListener("mouseenter", this.setNestedFormActive);
    }
  }

  setNestedFormActive({ target }) {
    if (this.activeCssClassValue) {
      this.element
        .querySelectorAll("[data-dynamic-nested-form-target=nestedForm].active")
        .forEach((activeForm) => {
          activeForm.classList.remove(this.activeCssClassValue);
        });
      target.classList.add(this.activeCssClassValue);
    }
  }

  addNestedField(cb?: (element: HTMLDivElement) => void) {
    const nestedFields = document.createElement("div");
    nestedFields.innerHTML = decodeURIComponent(atob(this.templateValue));

    const namePrefix = `[${this.associationNameValue}_attributes]`;
    const idPrefix = `_${this.associationNameValue}_attributes`;
    nestedFields
      .querySelectorAll<HTMLInputElement>(`[name*="${namePrefix}[0]"]`)
      .forEach((input) => {
        input.name = input.name.replace(
          `${namePrefix}[0]`,
          `${namePrefix}[${this.indexCounter}]`
        );
        input.id = input.id.replace(
          `${idPrefix}_0`,
          `${idPrefix}_${this.indexCounter}`
        );
      });
    nestedFields
      .querySelectorAll<HTMLLabelElement>(`[for^=${idPrefix}_`)
      .forEach((label) => {
        label.htmlFor = label.htmlFor.replace(
          `${idPrefix}_0`,
          `${idPrefix}_${this.indexCounter}`
        );
      });

    if (cb) {
      cb(nestedFields);
    }

    this.indexCounter += 1;

    const addedNodes: ChildNode[] = [];
    while (nestedFields.childNodes.length > 0) {
      const node = nestedFields.childNodes[0];
      this.fieldsContainerTarget.appendChild(node);
      addedNodes.push(node);
    }

    setTimeout(() => {
      addedNodes.forEach((node) => {
        node.dispatchEvent(new CustomEvent("dynamicNestedForm:added"));
      });
    });
  }

  deleteFields(event) {
    event.preventDefault();
    const nestedFields = event.currentTarget.closest(
      '[data-dynamic-nested-form-target="nestedForm"]'
    );

    if (!nestedFields) {
      throw 'No nestedFields found. Did you forget to add [data-dynamic-nested-form-target="nestedForm"] to the nested form element?';
    }

    this.deleteNestedFields(nestedFields);
  }

  deleteNestedFields(nestedFields) {
    const hiddenIdField = nestedFields.querySelector(
      'input[type=hidden][name$="[id]"]'
    );

    animateCSS(nestedFields, "fadeOutUp").then(() => {
      if (hiddenIdField.value) {
        nestedFields.querySelectorAll("input").forEach((input) => {
          if (input != hiddenIdField) {
            input.setAttribute("disabled", true);
          }
        });
        const destroyHidden = document.createElement("input");
        destroyHidden.setAttribute("hidden", "true");
        destroyHidden.setAttribute(
          "name",
          hiddenIdField.name.replace("[id]", "[_destroy]")
        );
        destroyHidden.setAttribute("value", "true");
        hiddenIdField.parentNode.insertBefore(destroyHidden, hiddenIdField);
        nestedFields.classList.add("hidden");
      } else {
        nestedFields.remove();
      }
      setTimeout(() => {
        this.dispatch("removed", { detail: { nestedFields } });
      });
    });
  }
}
